import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import {
  MessageSquare,
  Send,
  Inbox,
  ExternalLink,
  Calendar,
  User,
  Loader2,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Facebook,
  Instagram
} from 'lucide-react';
import toast from 'react-hot-toast';

interface SentComment {
  id: string;
  platform: string;
  generated_comment: string;
  published_at: string;
  from_account_name: string;
  to_account_name: string;
  post_url: string;
  post_caption: string;
  comment_id: string;
  comment_url: string;
}

interface ReceivedComment {
  id: string;
  platform: string;
  comment_text: string;
  received_at: string;
  from_account_name: string;
  to_account_name: string;
  post_url: string;
  post_caption: string;
}

interface DailyTotal {
  sent: number;
  received: number;
}

export function CommentExchangeHistory() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sentComments, setSentComments] = useState<SentComment[]>([]);
  const [receivedComments, setReceivedComments] = useState<ReceivedComment[]>([]);
  const [dailyTotals, setDailyTotals] = useState<DailyTotal>({ sent: 0, received: 0 });
  const [activeView, setActiveView] = useState<'sent' | 'received'>('sent');
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month' | 'all'>('today');

  useEffect(() => {
    if (user) {
      loadHistory();
    }
  }, [user, timeFilter]);

  const getTimeFilter = () => {
    const now = new Date();
    switch (timeFilter) {
      case 'today':
        return new Date(now.setHours(0, 0, 0, 0)).toISOString();
      case 'week':
        return new Date(now.setDate(now.getDate() - 7)).toISOString();
      case 'month':
        return new Date(now.setDate(now.getDate() - 30)).toISOString();
      default:
        return new Date(0).toISOString();
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);

      // Get user's accounts
      const { data: accounts } = await supabase
        .from('social_accounts')
        .select('id, page_name')
        .eq('owner_user_id', user.id);

      if (!accounts || accounts.length === 0) {
        setLoading(false);
        return;
      }

      const accountIds = accounts.map(a => a.id);
      const accountMap = new Map(accounts.map(a => [a.id, a.page_name]));
      const timeFilterDate = getTimeFilter();

      // Load sent comments
      const { data: sentData } = await supabase
        .from('comment_exchange_queue')
        .select(`
          id,
          platform,
          generated_comment,
          published_at,
          from_account_id,
          webhook_response,
          comment_exchange_posts_index (
            account_id,
            account_name,
            caption_text,
            permalink
          )
        `)
        .in('from_account_id', accountIds)
        .eq('status', 'done')
        .gte('published_at', timeFilterDate)
        .order('published_at', { ascending: false })
        .limit(100);

      const sent: SentComment[] = (sentData || []).map(c => ({
        id: c.id,
        platform: c.platform,
        generated_comment: c.generated_comment || '',
        published_at: c.published_at,
        from_account_name: accountMap.get(c.from_account_id) || 'Unknown',
        to_account_name: c.comment_exchange_posts_index?.account_name || 'Unknown',
        post_url: c.comment_exchange_posts_index?.permalink || '',
        post_caption: c.comment_exchange_posts_index?.caption_text || '',
        comment_id: c.webhook_response?.comment_id || '',
        comment_url: c.webhook_response?.comment_url || ''
      }));

      setSentComments(sent);

      // Load received comments (comments on our posts)
      const { data: receivedData } = await supabase
        .from('comment_exchange_queue')
        .select(`
          id,
          platform,
          generated_comment,
          published_at,
          from_account_id,
          comment_exchange_posts_index!inner (
            account_id,
            account_name,
            caption_text,
            permalink
          )
        `)
        .in('comment_exchange_posts_index.account_id', accountIds)
        .eq('status', 'done')
        .gte('published_at', timeFilterDate)
        .order('published_at', { ascending: false })
        .limit(100);

      // Get commenter account names
      const commenterIds = [...new Set(
        (receivedData || []).map(c => c.from_account_id)
      )];

      const { data: commenterAccounts } = await supabase
        .from('social_accounts')
        .select('id, page_name')
        .in('id', commenterIds);

      const commenterMap = new Map(
        (commenterAccounts || []).map(a => [a.id, a.page_name])
      );

      const received: ReceivedComment[] = (receivedData || [])
        .filter(c => c.from_account_id !== c.comment_exchange_posts_index?.account_id) // Exclude same-account comments
        .map(c => ({
          id: c.id,
          platform: c.platform,
          comment_text: c.generated_comment || '',
          received_at: c.published_at,
          from_account_name: commenterMap.get(c.from_account_id) || 'Unknown',
          to_account_name: c.comment_exchange_posts_index?.account_name || accountMap.get(c.comment_exchange_posts_index?.account_id) || 'Unknown',
          post_url: c.comment_exchange_posts_index?.permalink || '',
          post_caption: c.comment_exchange_posts_index?.caption_text || ''
        }));

      setReceivedComments(received);

      // Calculate totals
      setDailyTotals({
        sent: sent.length,
        received: received.length
      });

    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load comment history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <Facebook className="w-4 h-4 text-blue-600" />;
      case 'instagram':
        return <Instagram className="w-4 h-4 text-pink-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-600" />;
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-600">Comments Sent</span>
            <Send className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-blue-700">{dailyTotals.sent}</div>
          <div className="text-sm text-blue-600 mt-1">
            {timeFilter === 'today' ? 'Today' : timeFilter === 'week' ? 'This week' : timeFilter === 'month' ? 'This month' : 'All time'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-600">Comments Received</span>
            <Inbox className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-700">{dailyTotals.received}</div>
          <div className="text-sm text-green-600 mt-1">
            {timeFilter === 'today' ? 'Today' : timeFilter === 'week' ? 'This week' : timeFilter === 'month' ? 'This month' : 'All time'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-600">Exchange Rate</span>
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-purple-700">
            {dailyTotals.received > 0
              ? ((dailyTotals.sent / dailyTotals.received) * 100).toFixed(0)
              : '0'}%
          </div>
          <div className="text-sm text-purple-600 mt-1">Sent/Received ratio</div>
        </div>
      </div>

      {/* Time Filter */}
      <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200 w-fit">
        {(['today', 'week', 'month', 'all'] as const).map((filter) => (
          <button
            key={filter}
            onClick={() => setTimeFilter(filter)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              timeFilter === filter
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {filter === 'today' ? 'Today' : filter === 'week' ? 'Week' : filter === 'month' ? 'Month' : 'All Time'}
          </button>
        ))}
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200 w-fit">
        <button
          onClick={() => setActiveView('sent')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeView === 'sent'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Send className="w-4 h-4" />
          Sent ({sentComments.length})
        </button>
        <button
          onClick={() => setActiveView('received')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeView === 'received'
              ? 'bg-green-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Inbox className="w-4 h-4" />
          Received ({receivedComments.length})
        </button>
      </div>

      {/* Comments List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {activeView === 'sent' ? (
          sentComments.length === 0 ? (
            <div className="text-center py-12">
              <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Sent Comments</h3>
              <p className="text-sm text-gray-500">
                Comments you send will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sentComments.map((comment) => (
                <div key={comment.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {getPlatformIcon(comment.platform)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {comment.from_account_name}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm text-gray-600">
                          {comment.to_account_name}'s post
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatDate(comment.published_at)}
                        </span>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-2">
                        <p className="text-sm text-gray-700">{comment.generated_comment}</p>
                      </div>

                      {comment.post_caption && (
                        <div className="text-xs text-gray-500 mb-2">
                          On post: {truncateText(comment.post_caption, 80)}
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        {comment.post_url && (
                          <a
                            href={comment.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Post
                          </a>
                        )}
                        {comment.comment_url && (
                          <a
                            href={comment.comment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                          >
                            <MessageSquare className="w-3 h-3" />
                            View Comment
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          receivedComments.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Received Comments</h3>
              <p className="text-sm text-gray-500">
                Comments others send to your posts will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {receivedComments.map((comment) => (
                <div key={comment.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {getPlatformIcon(comment.platform)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {comment.from_account_name}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm text-gray-600">
                          Your post ({comment.to_account_name})
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatDate(comment.received_at)}
                        </span>
                      </div>

                      <div className="bg-green-50 rounded-lg p-3 mb-2">
                        <p className="text-sm text-gray-700">{comment.comment_text}</p>
                      </div>

                      {comment.post_caption && (
                        <div className="text-xs text-gray-500 mb-2">
                          On your post: {truncateText(comment.post_caption, 80)}
                        </div>
                      )}

                      {comment.post_url && (
                        <a
                          href={comment.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 w-fit"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Post
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
